import "./style.scss";
import { useState, useEffect, useRef, FC } from "react";
import { UseFormRegister, UseFormSetValue } from "react-hook-form";
import CloseSvg from "@/src/svg/CloseSvg";
import DeleteDarkSvg from "@/src/svg/DeleteDarkSvg";
import { FileFieldName, IApplyOnline } from "@/src/types/IApplyOnline";

interface IFileAttachmentModalProps {
  isActive: boolean;
  onClose: () => void;
  register: UseFormRegister<IApplyOnline>;
  setValue: UseFormSetValue<IApplyOnline>;
  selectedFilesMap: Record<FileFieldName, File[]>;
  setSelectedFilesMap: React.Dispatch<React.SetStateAction<Record<FileFieldName, File[]>>>;
}

const fileFields = [
  { name: "filePassport", title: "Международный паспорт" },
  { name: "fileCertificate", title: "Школьный сертификат" },
  { name: "fileEducation", title: "Предыдущее образование" },
  { name: "fileAnouther", title: "Дополнительные файлы" },
];

const FormSummaryModal: FC<IFileAttachmentModalProps> = ({
  isActive,
  onClose,
  register,
  setValue,
  selectedFilesMap,
  setSelectedFilesMap,
}) => {
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [localFilesMap, setLocalFilesMap] = useState<Record<FileFieldName, File[]>>({} as Record<FileFieldName, File[]>);

  useEffect(() => {
    if (isActive) {
      const clonedFilesMap: Record<FileFieldName, File[]> = {} as Record<FileFieldName, File[]>;
      for (const key in selectedFilesMap) {
        const typedKey = key as FileFieldName;
        clonedFilesMap[typedKey] = [...(selectedFilesMap[typedKey] || [])];
      }
      setLocalFilesMap(clonedFilesMap);
      document.body.classList.add("hidden");
    } else {
      document.body.classList.remove("hidden");
    }
  }, [isActive]);

  const handleFileChange = (name: string, files: FileList | null) => {
    if (!files) return;
    const newFiles = Array.from(files);
    const updated = [...(localFilesMap[name as FileFieldName] || []), ...newFiles];
    setLocalFilesMap((prev) => ({ ...prev, [name]: updated }));
  };

  const handleDeleteFile = (name: string, index: number) => {
    const updated = localFilesMap[name as FileFieldName].filter((_, i) => i !== index);
    setLocalFilesMap((prev) => ({ ...prev, [name]: updated }));
  };

  const handleAttachFiles = () => {
    setSelectedFilesMap(localFilesMap);
    Object.entries(localFilesMap).forEach(([key, files]) => {
      setValue(key as keyof IApplyOnline, files, {
        shouldValidate: true,
        shouldDirty: true,
      });
    });
    handleClose();
  };

  const handleClose = () => {
    document.body.classList.remove("hidden");
    onClose();
  };

  return (
    <div className={`form-summary-modal ${isActive ? "active" : ""}`}>
      <div className="form-summary-modal-bg" onClick={handleClose}></div>
      <div className="form-summary-modal-body">
        <div className="form-summary-modal-content">
          <div className="form-summary-modal-content-header">
            <div className="form-summary-modal-title">
              <p>File attachment</p>
              <div className="form-summary-modal-title-svg" onClick={handleClose}>
                <CloseSvg />
              </div>
            </div>

            <div className="form-summary-modal-subtitle">
              <p>Attach the necessary files for submission</p>
            </div>
          </div>
          <div className="form-summary-modal-items">
            {fileFields.map(({ name, title }) => (
              <div className="form-summary-modal-item" key={name}>
                <p className="form-summary-modal-item-title">{title}</p>
                <div className="form-summary-modal-item-content">
                  <input type="hidden" {...register(name as keyof IApplyOnline)} />

                  {localFilesMap[name as FileFieldName]?.length > 0 && (
                    <div className="form-summary-modal-item-files">
                      {localFilesMap[name as FileFieldName].map((file, index) => (
                        <div className="form-summary-modal-item-file" key={file.name + index}>
                          <p>{file.name}</p>
                          <div className="form-summary-modal-item-file-delete" onClick={() => handleDeleteFile(name, index)}>
                            <DeleteDarkSvg />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div
                    className="form-summary-modal-item-button"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleFileChange(name, e.dataTransfer.files);
                    }}
                    onClick={() => fileInputRefs.current[name]?.click()}
                  >
                    <input
                      type="file"
                      multiple
                      ref={(el) => {
                        fileInputRefs.current[name] = el;
                      }}
                      style={{ display: "none" }}
                      onChange={(e) => {
                        handleFileChange(name, e.target.files);
                        e.target.value = "";
                      }}
                    />
                    <p className="form-summary-modal-item-button-plus">+</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="form-summary-modal-content-add-btn" onClick={handleAttachFiles}>
            Прикрепить файлы →
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormSummaryModal;
